import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import './BlogSection.css';
import useReveal from './hooks/useReveal';

// An article only appears once it has a `link`. With none published, the section and its nav link stay hidden.
const ARTICLES = [
    {
        id: 1,
        title: "Building a RAG Pipeline for Project Management with pgvector",
        summary: "How I designed and implemented a Retrieval-Augmented Generation pipeline in SmartPM — from chunking strategy (750-token chunks, 120-token overlap) to semantic search with pgvector and grounding GPT-4o responses in real project data synced from connectors like Jira.",
        tags: ["AI", "RAG", "pgvector", "FastAPI", "PostgreSQL"],
        date: "June 2026",
        readTime: "8 min read",
        link: ""
    },
    {
        id: 2,
        title: "Multi-Agent AI Planning: Reconciling Estimates Against Reality",
        summary: "A deep dive into a three-agent GPT-4o pipeline that extracts estimated project phases from documentation, reconciles them against real Jira progress, and surfaces PM suggestions for human-in-the-loop review — all driven by Kafka events.",
        tags: ["AI", "Multi-Agent", "GPT-4o", "Kafka", "Python"],
        date: "May 2026",
        readTime: "10 min read",
        link: ""
    },
    {
        id: 3,
        title: "From Zero to Production: Deploying a Microservices App on AWS ECS Fargate",
        summary: "A practical guide to containerizing a Spring Boot + FastAPI + Angular stack and deploying it on AWS ECS Fargate with Cloud Map service discovery, Kafka, and Redis — including the pitfalls I hit (Kafka quorum voters, missing env vars, service discovery) and how I fixed them.",
        tags: ["AWS", "Docker", "Microservices", "Kafka", "DevOps"],
        date: "April 2026",
        readTime: "12 min read",
        link: ""
    },
    {
        id: 4,
        title: "Explainable Risk Detection: Pairing XGBoost with SHAP",
        summary: "Why a black-box risk score isn't enough for project governance. How I built a risk-detection layer using XGBoost and Isolation Forest, then wired SHAP (TreeExplainer on multiclass XGBoost) through FastAPI so every prediction ships with an interpretable rationale.",
        tags: ["Machine Learning", "XGBoost", "SHAP", "FastAPI", "Explainability"],
        date: "May 2026",
        readTime: "9 min read",
        link: ""
    },
    {
        id: 5,
        title: "Compiling Natural Language into Automation Rules",
        summary: "Inside SmartPM's AI Trigger system: how user-written conditions in plain language are compiled into structured condition/formula JSON via an LLM, then evaluated by a FastAPI pipeline — complete with an expression-tree visualizer and a formula compiler on the Angular frontend.",
        tags: ["AI", "DSL", "FastAPI", "Angular", "LLM"],
        date: "April 2026",
        readTime: "7 min read",
        link: ""
    },
    {
        id: 6,
        title: "Building a Resilient Async Email Pipeline with Kafka",
        summary: "Fire-and-forget email done right. How I designed an async Kafka email pipeline with retry topics, a dead-letter queue, an idempotency cache to prevent duplicate sends, and Micrometer metrics for full observability.",
        tags: ["Kafka", "Spring Boot", "Resilience", "Observability", "Backend"],
        date: "March 2026",
        readTime: "8 min read",
        link: ""
    },
    {
        id: 7,
        title: "Real-Time Updates with WebSocket, STOMP & JWT Auth",
        summary: "How I implemented live project updates over WebSocket using STOMP and SockJS, enforcing JWT authentication at the STOMP frame level so every real-time channel stays secure without sacrificing responsiveness.",
        tags: ["WebSocket", "STOMP", "JWT", "Spring Boot", "Security"],
        date: "April 2026",
        readTime: "6 min read",
        link: ""
    },
    {
        id: 8,
        title: "Meeting Intelligence: Turning Transcripts into Structured Insights",
        summary: "Building a transcript-processing pipeline with gpt-4o-mini that distills raw meeting transcripts into structured, actionable insights — including how I handled token-limit constraints and chunked long transcripts without losing context.",
        tags: ["AI", "NLP", "GPT-4o-mini", "FastAPI", "Python"],
        date: "June 2026",
        readTime: "7 min read",
        link: ""
    }
];

export const PUBLISHED_ARTICLES = ARTICLES.filter(a => a.link);

const BlogSection = () => {
    const { t } = useTranslation();
    const revealRef = useReveal();

    if (PUBLISHED_ARTICLES.length === 0) return null;

    return (
        <section className="section blog" id="Blog" ref={revealRef}>
            <div className="container">
                <div className="section-header-row">
                    <div className="section-header">
                        <span className="eyebrow">{t('blog.eyebrow')}</span>
                        <h2 className="section-title">{t('blog.title')}</h2>
                        <p className="section-subtitle">{t('blog.subtitle')}</p>
                    </div>
                </div>

                <div className="blog-grid">
                    {PUBLISHED_ARTICLES.map(article => (
                        <article key={article.id} className="blog-card reveal">
                            <p className="blog-meta">{article.date} · {article.readTime}</p>
                            <h3 className="blog-card-title">{article.title}</h3>
                            <p className="blog-card-summary">{article.summary}</p>
                            <div className="blog-card-footer">
                                <div className="blog-tags">
                                    {article.tags.slice(0, 3).map(tag => <span key={tag} className="tag">{tag}</span>)}
                                </div>
                                <a href={article.link} target="_blank" rel="noopener noreferrer" className="text-link">
                                    {t('blog.read_more')}
                                    <ArrowRight size={13} className="flip-rtl" />
                                </a>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BlogSection;
